
import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { City } from '../../data/types/city-types';
import { getLowOrUnreachedCities } from '../../utils/city-utils';
import '../../styles/map-pins.css';

interface MapMarkersGenericProps {
  map: mapboxgl.Map;
  cities?: City[];
  isGlobalView?: boolean;
  showFunding?: boolean;
  showLowPresence?: boolean;
  showUnreached?: boolean;
  showAreasOfNeed?: boolean;
}

const MapMarkersGeneric: React.FC<MapMarkersGenericProps> = ({
  map,
  cities = [],
  isGlobalView = false,
  showFunding = true,
  showLowPresence = true,
  showUnreached = true,
  showAreasOfNeed = true
}) => {
  useEffect(() => {
    if (!map) return;
    
    // Clean up any existing layers and sources
    const cleanupLayers = () => {
      const layersToRemove = [
        'region-fills', 
        'chiapas-fill', 
        'hidalgo-fill',
        'chis-highlight',
        'hgo-highlight',
        'filtered-city-dots',
        'filtered-city-halos',
        'filtered-city-labels',
        'filtered-city-pins'
      ];
      
      layersToRemove.forEach(layerId => {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      });
      
      if (map.getSource('filtered-cities')) {
        map.removeSource('filtered-cities');
      }
    };
    
    // Create pin images for different markers
    const createPinImages = () => {
      if (!map.hasImage('pin-orange')) {
        createPinImage(map, '#F97316', 'pin-orange', map);
        createPinImage(map, '#ea384c', 'pin-red', map);
        createPinImage(map, '#9b87f5', 'pin-purple', map);
        createPinImage(map, '#3b82f6', 'pin-blue', map);
      }
    };
    
    // Add markers to the map
    const addMarkers = () => {
      // Use provided cities or get all low/unreached cities across Mexico
      const filteredCities = cities.length > 0 ? cities : getLowOrUnreachedCities();
      
      console.log("MapMarkersGeneric: Displaying", filteredCities.length, "low/unreached cities");
      
      // Add areas of need (this would come from real data in a production app)
      const areasOfNeed = [
        // Sample areas of need - in a real app, these would come from API data
        // Format: [longitude, latitude, name, region_id]
        [-99.1332, 19.4326, "Mexico City - Low Income Areas", "CDMX"],
        [-106.4245, 31.6904, "Ciudad Juárez - Border Areas", "CHIH"],
        [-89.6170, 20.9670, "Mérida - Rural Outskirts", "YUC"],
        [-103.3496, 20.6597, "Guadalajara - Urban Poor", "JAL"],
        [-100.3161, 25.6866, "Monterrey - Marginalized Communities", "NL"],
        
        // Adding more global areas of need for worldwide view
        // Africa
        [28.0339, 1.6596, "Algeria - Sahara Region", "DZA"],
        [37.9062, 0.0236, "Kenya - Northern Regions", "KEN"],
        [8.6753, 9.0820, "Nigeria - Middle Belt", "NGA"],
        [30.8025, 26.8206, "Egypt - Upper Egypt", "EGY"],
        [18.7322, -33.9249, "South Africa - Cape Town Townships", "ZAF"],
        
        // Asia
        [104.1954, 35.8617, "China - Western Provinces", "CHN"],
        [78.9629, 20.5937, "India - Rural Areas", "IND"],
        [69.3451, 30.3753, "Pakistan - Balochistan", "PAK"],
        [103.8198, 1.3521, "Singapore - Migrant Worker Areas", "SGP"],
        [100.9925, 15.8700, "Thailand - Northern Hill Tribes", "THA"],
        
        // Europe (adding more locations)
        [2.3522, 48.8566, "Paris - Suburban Areas", "FRA"],
        [13.4050, 52.5200, "Berlin - Immigrant Communities", "DEU"],
        [12.4964, 41.9028, "Rome - Disadvantaged Areas", "ITA"],
        [-0.1278, 51.5074, "London - East End", "GBR"],
        [4.9041, 52.3676, "Amsterdam - Urban Margins", "NLD"],
        [18.0686, 59.3293, "Stockholm - Immigrant Communities", "SWE"],
        [37.6173, 55.7558, "Moscow - Outer Districts", "RUS"],
        [21.0122, 52.2297, "Warsaw - Post-industrial Areas", "POL"],
        [-3.7038, 40.4168, "Madrid - Southern Districts", "ESP"],
        [19.0402, 47.4979, "Budapest - Eighth District", "HUN"],
        [14.4378, 50.0755, "Prague - Excluded Areas", "CZE"],
        [23.7275, 37.9838, "Athens - Refugee Communities", "GRC"],

        // More in Americas
        [-74.0060, 40.7128, "New York - South Bronx", "USA"],
        [-118.2437, 34.0522, "Los Angeles - Skid Row", "USA"],
        [-87.6298, 41.8781, "Chicago - South Side", "USA"],
        [-79.3832, 43.6532, "Toronto - Jane and Finch", "CAN"],
        [-58.3816, -34.6037, "Buenos Aires - Villa Miseria Areas", "ARG"],
        [-70.6693, -33.4489, "Santiago - Peripheral Communities", "CHL"],
        [-46.6333, -23.5505, "São Paulo - Favelas", "BRA"],
        
        // Oceania
        [151.2093, -33.8688, "Sydney - Western Suburbs", "AUS"],
        [174.7762, -36.8485, "Auckland - South Auckland", "NZL"]
      ];
      
      // Filter areas of need if toggle is off
      const filteredAreasOfNeed = showAreasOfNeed ? areasOfNeed : [];
      
      // Build GeoJSON features from cities and areas of need, applying visibility filters
      const features = buildFeatures(
        filteredCities, 
        filteredAreasOfNeed, 
        map,
        showLowPresence,
        showUnreached
      );
      
      // Add the source to the map
      addGeoJsonSource(features, map);
      
      // Add marker layers to the map
      addMarkerLayers(showFunding, isGlobalView, map);
      
      // Add hover interactions
      setupHoverInteractions(map);
      
      // Add click interactions
      setupClickInteractions(map);
    };
    
    // Initialize pin display
    cleanupLayers();
    
    // Wait for the map style to be fully loaded before adding images and layers
    if (map.isStyleLoaded()) {
      createPinImages();
      addMarkers();
    } else {
      map.once('style.load', () => {
        createPinImages();
        addMarkers();
      });
    }
    
    // Clean up when component unmounts
    return () => {
      if (!map || !map.getStyle()) return; // Check if map still exists and has style
      
      cleanupLayers();
      
      // Remove event listeners
      map.off('mouseenter', 'filtered-city-pins');
      map.off('mouseleave', 'filtered-city-pins');
      map.off('click', 'filtered-city-pins');
      
      // Remove custom images
      ['pin-orange', 'pin-red', 'pin-purple', 'pin-blue'].forEach(id => {
        if (map.hasImage(id)) map.removeImage(id);
      });
    };
  }, [map, cities, isGlobalView, showFunding, showLowPresence, showUnreached, showAreasOfNeed]);
  
  return null;
};

// Helper functions moved out of the main effect for better organization

function createPinImage(map: mapboxgl.Map, color: string, id: string, mapInstance: mapboxgl.Map) {
  const size = 24;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  
  if (ctx) {
    // Clear canvas
    ctx.clearRect(0, 0, size, size);
    
    // Draw a solid pin - simpler design with stronger colors
    // Pin head (circle)
    ctx.beginPath();
    ctx.arc(size/2, size/3, size/3.5, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    
    // Pin border - solid white border for better visibility
    ctx.beginPath();
    ctx.arc(size/2, size/3, size/3.5, 0, Math.PI * 2);
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    
    // Pin point (triangle shape)
    ctx.beginPath();
    ctx.moveTo(size/2 - size/5, size/3);
    ctx.lineTo(size/2, size - size/6);
    ctx.lineTo(size/2 + size/5, size/3);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.closePath();
    
    // Clean outline for the entire pin
    ctx.beginPath();
    ctx.moveTo(size/2 - size/5, size/3);
    ctx.lineTo(size/2, size - size/6);
    ctx.lineTo(size/2 + size/5, size/3);
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.closePath();
    
    // Add the image to the map
    try {
      mapInstance.addImage(id, { 
        data: ctx.getImageData(0, 0, size, size).data, 
        width: size, 
        height: size 
      });
    } catch (error) {
      console.error(`Error adding image ${id} to map:`, error);
    }
  }
}

function buildFeatures(
  cities: City[], 
  areasOfNeed: any[], 
  map: mapboxgl.Map,
  showLowPresence: boolean = true,
  showUnreached: boolean = true
) {
  // Filter cities based on toggle states
  const filteredCities = cities.filter(city => {
    if (city.church_presence === 'Low' && !showLowPresence) return false;
    if (city.church_presence === 'Unreached' && !showUnreached) return false;
    return true;
  });
  
  return [
    ...filteredCities.map(city => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: city.coordinates
      },
      properties: {
        name: city.name,
        church_presence: city.church_presence,
        region_id: city.region_id,
        funding_amount: city.funding_amount || 0,
        city_id: city.city_id || city.id,
        is_need_area: false
      }
    })),
    ...areasOfNeed.map(area => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [area[0], area[1]]
      },
      properties: {
        name: area[2],
        region_id: area[3],
        funding_amount: Math.floor(Math.random() * 100000) + 50000, // Random funding for demo
        is_need_area: true,
        church_presence: 'Need' // Add this to filter by presence
      }
    }))
  ];
}

function addGeoJsonSource(features: any[], map: mapboxgl.Map) {
  if (map.getSource('filtered-cities')) {
    map.removeSource('filtered-cities');
  }
  
  try {
    map.addSource('filtered-cities', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features
      }
    });
  } catch (error) {
    console.error("Error adding source:", error);
  }
}

function addMarkerLayers(showFunding: boolean, isGlobalView: boolean, map: mapboxgl.Map) {
  try {
    // Add symbols with pin images
    map.addLayer({
      id: 'filtered-city-pins',
      type: 'symbol',
      source: 'filtered-cities',
      layout: {
        'icon-image': [
          'case',
          ['==', ['get', 'is_need_area'], true], 'pin-purple',
          ['==', ['get', 'church_presence'], 'Low'], 'pin-orange',
          ['==', ['get', 'church_presence'], 'Unreached'], 'pin-red',
          'pin-blue' // Default
        ],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2, 0.6,  // Slightly larger at global zoom for better visibility
          4, 0.8,  // Medium at country zoom
          7, 0.9,  // Larger at region zoom
          10, 1.0  // Largest at city zoom
        ],
        'icon-allow-overlap': true,
        'icon-anchor': 'bottom',
        'icon-offset': [0, 0]
      },
      paint: {
        // No specific paint properties for symbols
      }
    });
    
    // Add data-presence attribute to each pin for CSS selection
    map.on('render', () => {
      document.querySelectorAll('.mapboxgl-marker').forEach(marker => {
        if (marker.getAttribute('data-presence-set') !== 'true') {
          const features = map.queryRenderedFeatures(
            map.project((marker as any)._lngLat),
            { layers: ['filtered-city-pins'] }
          );
          
          if (features.length > 0 && features[0].properties) {
            const presence = features[0].properties.church_presence;
            if (presence) {
              marker.setAttribute('data-presence', presence);
              marker.setAttribute('data-presence-set', 'true');
            }
          }
        }
      });
    });
    
    // Add a subtle shadow circle for the pins for better visibility
    map.addLayer({
      id: 'filtered-city-shadows',
      type: 'circle',
      source: 'filtered-cities',
      paint: {
        'circle-radius': 8,
        'circle-color': 'rgba(255, 255, 255, 0.4)',
        'circle-blur': 0.5,
        'circle-opacity': 0.4
      }
    }, 'filtered-city-pins');
    
    // Only add funding labels if they should be shown
    if (showFunding) {
      // Add a text layer for funding amounts
      map.addLayer({
        id: 'filtered-city-labels',
        type: 'symbol',
        source: 'filtered-cities',
        layout: {
          'text-field': [
            'format',
            ['get', 'name'],
            { 'font-scale': 0.8 },
            '\n',
            {},
            ['concat', '$', ['to-string', ['get', 'funding_amount']]],
            { 'font-scale': 0.7 }
          ],
          'text-font': ['Open Sans Regular'],
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
          'text-size': isGlobalView ? 10 : 12,
          'text-allow-overlap': false,
          'text-ignore-placement': false,
        },
        paint: {
          'text-color': '#1EAEDB',
          'text-halo-color': 'rgba(255, 255, 255, 0.9)',
          'text-halo-width': 2
        }
      });
    }
  } catch (error) {
    console.error("Error adding marker layers:", error);
  }
}

function setupHoverInteractions(map: mapboxgl.Map) {
  // Create a popup but don't add it to the map yet
  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
    className: 'city-popup-hover'
  });

  map.on('mouseenter', 'filtered-city-pins', (e) => {
    map.getCanvas().style.cursor = 'pointer';
    
    if (e.features && e.features[0]) {
      const coordinates = e.features[0].geometry.coordinates.slice() as [number, number];
      const name = e.features[0].properties.name;
      const presence = e.features[0].properties.church_presence || 'Need Area';
      const funding = e.features[0].properties.funding_amount;
      const isNeedArea = e.features[0].properties.is_need_area;
      
      let statusClass = 'bg-orange-500';
      let statusText = 'Low Presence';
      
      if (isNeedArea) {
        statusClass = 'bg-purple-500';
        statusText = 'Area of Need';
      } else if (presence === 'Unreached') {
        statusClass = 'bg-red-500';
        statusText = 'Unreached';
      }
      
      popup.setLngLat(coordinates)
        .setHTML(`
          <div class="px-3 py-2 bg-white/95 backdrop-blur-sm rounded shadow-lg">
            <h3 class="font-medium text-sm">${name}</h3>
            <div class="flex items-center gap-1 mt-1">
              <span class="inline-block w-2 h-2 rounded-full ${statusClass}"></span>
              <span class="text-xs text-gray-600">${statusText}</span>
            </div>
            <div class="mt-1 text-xs font-medium text-blue-500">$${funding.toLocaleString()}</div>
          </div>
        `)
        .addTo(map);
    }
  });

  map.on('mouseleave', 'filtered-city-pins', () => {
    map.getCanvas().style.cursor = '';
    popup.remove();
  });
}

function setupClickInteractions(map: mapboxgl.Map) {
  map.on('click', 'filtered-city-pins', (e) => {
    if (e.features && e.features[0]) {
      const cityId = e.features[0].properties.city_id;
      const regionId = e.features[0].properties.region_id;
      
      // Dispatch a custom event that can be captured by the app
      const event = new CustomEvent('cityClick', {
        detail: { cityId, regionId }
      });
      document.dispatchEvent(event);
    }
  });
}

export default MapMarkersGeneric;
